import { useCallback } from 'react';
import { useLocation, type NavigateOptions } from 'react-router-dom';

import { useModals, type ModalPath, type Params, type Path } from '../router';

interface UseModalNavigationOptions<TPath extends Path> {
  open?: NavigateOptions & { params: TPath extends keyof Params ? Params[TPath] : never };
  close?: NavigateOptions & { params: TPath extends keyof Params ? Params[TPath] : never };
}

function useModalNavigation<TPath extends Path>(
  path: TPath,
  modalPath: ModalPath,
  options: UseModalNavigationOptions<TPath> = {},
) {
  const modals = useModals();
  const location = useLocation();

  const { open: openOptions, close: closeOptions } = options;

  const open = useCallback(() => {
    // use any type due to type conflict with Generouted Path type since location.pathname is the current path
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const locationPath = `${location.pathname}${location.search}` as any;
    modals.open(modalPath, {
      at: locationPath,
      preventScrollReset: true,
      replace: true,
      ...openOptions,
    });
  }, [modals, openOptions]);

  const close = useCallback(() => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const path = `${location.pathname}${location.search}` as any;
    modals.close({ at: path, preventScrollReset: true, state: {}, replace: true, ...closeOptions });
  }, [modals, closeOptions]);

  return { open, close, current: modals.current };
}

export { useModalNavigation };
